import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, forkJoin, map, Observable, of, shareReplay, startWith, switchMap, tap} from 'rxjs';
import { DEFAULT_PAGING, Logger, OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import {buildClientKey, buildFilterEnabledAttributesViewModel, buildStoreAttributesEditViewModel, enrichFilterCategoriesWithSourceOptions, normalizeDisplayColor, readCategoryDisplayColor, StoreAttributeCategory, StoreAttributeCategoryViewModel, StoreAttributeDomain,
  StoreAttributeDomainSavePayload, StoreAttributeEditCategoryViewModel, StoreAttributeFilterCategoryViewModel, StoreAttributeSummaryStats, StoreAttributeTypeViewModel, StoreAttributeTypesViewModel, StoreSourceLookupOption, VALUE_TYPE_DIALOG_LABELS } from '../models/store-attribute.model';
import { Store } from '../models/store.model';
import { StoreDomain } from '../models/store-domain.model';
import { StoreService } from './store.service';

const log = new Logger('StoreAttributeService');

export interface FilterEnabledAttributesState {
  categories: StoreAttributeFilterCategoryViewModel[];
  loading: boolean;
  loadFailed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StoreAttributeService {
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({
    ...DEFAULT_PAGING,
    pageSize: 500,
  });
  private sortBehaviorSubject = new BehaviorSubject({
    active: 'AttributeTypeName',
    direction: 'asc' as 'asc' | 'desc',
  });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');

  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public reload$ = this.reloadBehaviorSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private organizationService: OrganizationService,
    private storeService: StoreService<Store, StoreDomain<Store>>,
  ) {}

  private apiBase(): string {
    return `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}`;
  }

  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged(
      (previous, current) => JSON.stringify(previous) === JSON.stringify(current),
    ),
    map(([page, sort, search]) => {
      let params: HttpParams = new HttpParams({
        fromObject: {
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $count: true,
          $expand: 'Detail',
          $orderby: `Detail/${sort.active} ${sort.direction}`,
        },
      });

      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    }),
  );

  sort(sort: { active: string; direction: string }): void {
    this.sortBehaviorSubject.next({
      active: sort.active,
      direction: (sort.direction || 'asc') as 'asc' | 'desc',
    });
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  reload(): void {
    this.reloadBehaviorSubject.next(uuidv4());
  }

  page(page: any): void {
    this.pageBehaviorSubject.next(page);
  }

  search(search: string): void {
    this.searchBehaviorSubject.next(search);
    const paging = JSON.parse(JSON.stringify(this.pageBehaviorSubject.getValue()));
    paging.pageIndex = 0;
    this.pageBehaviorSubject.next(paging);
  }

  private combinedResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap((params) =>
      forkJoin({
        domains: this.httpClient.get<any>(
          `${this.apiBase()}/StoreAttributeDomains/GetAll`,
          { params },
        ),
        categories: this.httpClient.get<StoreAttributeCategory[]>(
          `${this.apiBase()}/StoreAttributeCategories/GetAllCategories`,
        ),
      }),
    ),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay(1),
  );

  public storeAttributeDomains$: Observable<StoreAttributeDomain[]> =
    this.combinedResponse$.pipe(
      map((res) => {
        const domains = res.domains?.value ?? res.domains ?? [];
        return Array.isArray(domains) ? domains : [];
      }),
    );

  public categories$: Observable<StoreAttributeCategory[]> =
    this.combinedResponse$.pipe(
      map((res) =>
        this.normalizeCollectionResponse<Record<string, unknown>>(res.categories).map(
          (item) => this.normalizeCategory(item),
        ),
      ),
    );

  public totalRecords$: Observable<number> = this.combinedResponse$.pipe(
    map((res) => res.domains?.['@odata.count'] ?? 0),
  );

  public viewModel$: Observable<StoreAttributeTypesViewModel> = combineLatest([
    this.storeAttributeDomains$,
    this.categories$,
    this.isLoading$,
    this.totalRecords$,
  ]).pipe(
    map(([domains, categories, isLoading, totalRecords]) => ({
      categories: this.buildCategoryViewModels(domains, categories),
      stats: this.buildSummaryStats(domains, categories),
      isLoading,
      totalRecords,
    })),
  );

  createCategory(category: StoreAttributeCategory): Observable<StoreAttributeCategory> {
    const payload: StoreAttributeCategory = {
      ...category,
      ClientKey: category.ClientKey || buildClientKey(category.CategoryName),
    };
    return this.httpClient
      .post<Record<string, unknown>>(`${this.apiBase()}/StoreAttributeCategories`, payload)
      .pipe(map((res) => this.normalizeCategory(res)));
  }

  updateCategory(
    id: string,
    category: StoreAttributeCategory,
  ): Observable<StoreAttributeCategory> {
    return this.httpClient
      .put<Record<string, unknown>>(`${this.apiBase()}/StoreAttributeCategories/${id}`, {
        ...category,
        Id: id,
      })
      .pipe(map((res) => this.normalizeCategory(res)));
  }

  reorderCategories(orderedCategoryIds: string[]): Observable<StoreAttributeCategory[]> {
    return this.httpClient
      .post<unknown>(`${this.apiBase()}/StoreAttributeCategories/ReorderCategories`, orderedCategoryIds)
      .pipe(
        map((res) =>
          this.normalizeCollectionResponse<Record<string, unknown>>(res).map((item) =>
            this.normalizeCategory(item),
          ),
        ),
      );
  }

  deleteCategory(id: string): Observable<unknown> {
    return this.httpClient.delete(
      `${this.apiBase()}/StoreAttributeCategories/${id}`,
    );
  }

  getAllCategories(): Observable<StoreAttributeCategory[]> {
    return this.httpClient
      .get<unknown>(`${this.apiBase()}/StoreAttributeCategories/GetAllCategories`)
      .pipe(
        map((res) =>
          this.normalizeCollectionResponse<Record<string, unknown>>(res).map(
            (item) => this.normalizeCategory(item),
          ),
        ),
      );
  }

  createDomain(payload: StoreAttributeDomainSavePayload): Observable<StoreAttributeDomain> {
    const detail = {
      ...payload.Detail,
      ClientKey:
        payload.Detail.ClientKey ||
        buildClientKey(payload.Detail.AttributeTypeName),
    };
    return this.httpClient.post<StoreAttributeDomain>(
      `${this.apiBase()}/StoreAttributeDomains`,
      { ...payload, Detail: detail },
    );
  }

  updateDomainFilterProperty(
    id: string,
    enableFilter: boolean,
  ): Observable<StoreAttributeDomain> {
    const params = new HttpParams({
      fromObject: { key: id, enableFilter: String(enableFilter) },
    });
    return this.httpClient.post<StoreAttributeDomain>(
      `${this.apiBase()}/StoreAttributeDomains/UpdateFilterProperty`,
      null,
      { params },
    );
  }

  updateDomainValues(
    id: string,
    payload: StoreAttributeDomainSavePayload,
  ): Observable<StoreAttributeDomain> {
    const params = new HttpParams({ fromObject: { key: id } });
    return this.httpClient.post<StoreAttributeDomain>(
      `${this.apiBase()}/StoreAttributeDomains/UpdateValues`,
      { ...payload, Detail: { ...payload.Detail, Id: id } },
      { params },
    );
  }

  deleteDomain(id: string): Observable<unknown> {
    return this.httpClient.delete(
      `${this.apiBase()}/StoreAttributeDomains/${id}`,
    );
  }

  getAttributeDefinitionsForStoreEdit(): Observable<StoreAttributeDomain[]> {
    const params = new HttpParams({
      fromObject: {
        $expand: 'Detail',
        $top: '500',
        $orderby: 'CategoryName asc',
      },
    });

    return this.httpClient
      .get<unknown>(`${this.apiBase()}/StoreAttributeDomains/GetAll`, { params })
      .pipe(
        map((res) => this.normalizeCollectionResponse<StoreAttributeDomain>(res)),
        map((domains) =>
          domains.filter((domain) => !!domain.Detail?.ClientKey?.trim()),
        ),
      );
  }

  getStoreAttributesEditViewModel(): Observable<StoreAttributeEditCategoryViewModel[]> {
    return forkJoin({
      domains: this.getAttributeDefinitionsForStoreEdit(),
      categories: this.getAllCategories(),
    }).pipe(
      map(({ domains, categories }) =>
        buildStoreAttributesEditViewModel(domains, categories),
      ),
    );
  }

  private filterEnabledAttributesState$ = this.reloadBehaviorSubject.pipe(
    switchMap(() =>
      this.loadFilterEnabledAttributesState().pipe(
        startWith({
          categories: [] as StoreAttributeFilterCategoryViewModel[],
          loading: true,
          loadFailed: false,
        }),
      ),
    ),
    shareReplay(1),
  );

  private loadFilterEnabledAttributesState(): Observable<FilterEnabledAttributesState> {
    let coreLoadFailed = false;

    return forkJoin({
      domains: this.getAttributeDefinitionsForStoreEdit().pipe(
        catchError((error) => {
          coreLoadFailed = true;
          log.error('Failed to load store attribute domains for filtering', error);
          return of([] as StoreAttributeDomain[]);
        }),
      ),
      categories: this.getAllCategories().pipe(
        catchError((error) => {
          coreLoadFailed = true;
          log.error('Failed to load store attribute categories for filtering', error);
          return of([] as StoreAttributeCategory[]);
        }),
      ),
    }).pipe(
      switchMap(({ domains, categories }) => {
        const base = buildFilterEnabledAttributesViewModel(domains, categories);

        return forkJoin({
          cities: this.storeService.cities$.pipe(
            catchError((error) => {
              log.error('Failed to load cities for attribute filters', error);
              return of([] as string[]);
            }),
          ),
          states: this.storeService.states$.pipe(
            catchError((error) => {
              log.error('Failed to load states for attribute filters', error);
              return of([] as string[]);
            }),
          ),
          banners: this.storeService.getBanners().pipe(
            map((res) => this.normalizeCollectionResponse<StoreSourceLookupOption>(res)),
            catchError((error) => {
              log.error('Failed to load banners for attribute filters', error);
              return of([] as StoreSourceLookupOption[]);
            }),
          ),
          divisions: this.storeService.getDivisions().pipe(
            map((res) => this.normalizeCollectionResponse<StoreSourceLookupOption>(res)),
            catchError((error) => {
              log.error('Failed to load divisions for attribute filters', error);
              return of([] as StoreSourceLookupOption[]);
            }),
          ),
        }).pipe(
          map(({ cities, states, banners, divisions }) =>
            enrichFilterCategoriesWithSourceOptions(base, {
              cities,
              states,
              countries: [],
              banners,
              divisions,
            }),
          ),
        );
      }),
      map((categories) => ({
        categories,
        loading: false,
        loadFailed: coreLoadFailed,
      })),
      catchError((error) => {
        log.error('Failed to load filter-enabled attributes view model', error);
        return of({
          categories: [] as StoreAttributeFilterCategoryViewModel[],
          loading: false,
          loadFailed: true,
        });
      }),
    );
  }

  getFilterEnabledAttributesState(): Observable<FilterEnabledAttributesState> {
    return this.filterEnabledAttributesState$;
  }

  getFilterEnabledAttributesViewModel(): Observable<StoreAttributeFilterCategoryViewModel[]> {
    return this.getFilterEnabledAttributesState().pipe(
      map((state) => state.categories),
    );
  }

  private normalizeCollectionResponse<T>(res: unknown): T[] {
    if (Array.isArray(res)) {
      return res;
    }
    const value = (res as { value?: unknown })?.value;
    return Array.isArray(value) ? value : [];
  }

  private normalizeCategory(raw: Record<string, unknown>): StoreAttributeCategory {
    const category = raw as Partial<StoreAttributeCategory> & { displayColor?: string };
    return {
      Id: (raw['Id'] ?? raw['id']) as string | undefined,
      ClientKey: (raw['ClientKey'] ?? raw['clientKey']) as string | undefined,
      CategoryName: (raw['CategoryName'] ?? raw['categoryName']) as string,
      Description: (raw['Description'] ?? raw['description']) as string | undefined,
      DisplayColor: readCategoryDisplayColor(category),
      DisplayOrder: (raw['DisplayOrder'] ?? raw['displayOrder']) as number | undefined,
    };
  }

  private buildSummaryStats(
    domains: StoreAttributeDomain[],
    categories: StoreAttributeCategory[],
  ): StoreAttributeSummaryStats {
    const totalValues = domains.reduce(
      (sum, domain) => sum + (domain.Detail?.Values?.length ?? 0),
      0,
    );
    const activeInFilters = domains.filter(
      (domain) => domain.Detail?.FilterEnabled,
    ).length;

    return {
      categoryCount: categories.length,
      typeCount: domains.length,
      totalValues,
      activeInFilters,
    };
  }

  private buildCategoryViewModels(
    domains: StoreAttributeDomain[],
    categories: StoreAttributeCategory[],
  ): StoreAttributeCategoryViewModel[] {
    const sortedCategories = [...categories].sort(
      (a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0),
    );

    return sortedCategories.map((category) => {
      const categoryId = category.Id ?? '';
      const attributeTypes = domains
        .filter((domain) => domain.CategoryId === categoryId)
        .map((domain) => this.mapTypeViewModel(domain))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        category,
        id: categoryId,
        name: category.CategoryName,
        color: normalizeDisplayColor(readCategoryDisplayColor(category)),
        description: category.Description,
        displayOrder: category.DisplayOrder ?? 0,
        attributeTypes,
        collapsedSummary: this.buildCollapsedSummary(attributeTypes),
      };
    });
  }

  private mapTypeViewModel(domain: StoreAttributeDomain): StoreAttributeTypeViewModel {
    const detail = domain.Detail!;
    const valueType = detail.ValueType;

    return {
      domain,
      id: detail.Id ?? domain.DomainId ?? '',
      name: detail.AttributeTypeName,
      valueType,
      valueTypeLabel: VALUE_TYPE_DIALOG_LABELS[valueType] ?? valueType,
      sourceField: detail.ValueSourceField,
      filterEnabled: detail.FilterEnabled,
      values: (detail.Values ?? []).map((label) => ({
        label,
        storeCount: 0,
      })),
    };
  }

  private buildCollapsedSummary(
    attributeTypes: StoreAttributeTypeViewModel[],
  ): string {
    if (!attributeTypes.length) {
      return 'No attribute types';
    }

    const names = attributeTypes.map((type) => type.name);
    const preview = names.slice(0, 3).join(', ');
    if (names.length <= 3) {
      return `${names.length} attribute type${names.length === 1 ? '' : 's'} - ${preview}`;
    }

    return `${names.length} attribute types - ${preview}...`;
  }
}
